<template>
  <v-card color="darknavyblue" elevation="5" class="pa-4 mb-4 overflow-visible">
    <v-card-title>Domain Program Metadata ({{ currentEnvironment?.name }})</v-card-title>
    <v-card-text>
      <v-progress-circular v-if="loading" indeterminate />
      <p v-else-if="!loadedMetadata" class="opacity-50">
        Metadata not found.
      </p>
      <template v-else>
        <div class="mb-4 text-base">
          <p><strong>Metadata</strong></p>
          <ProgramMetadataField
            ref="metadataFieldRef"
            v-model="localMetadata"
            :message="ProgramEnvironmentMetadata"
          />
        </div>
        <div class="d-flex justify-end ga-2">
          <v-btn
            v-if="loadedMetadata"
            variant="text"
            :disabled="loading || saving || !hasUnsavedChanges"
            @click="resetForm(loadedMetadata)"
          >
            Reset
          </v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="saveDisabled"
            @click="saveMetadata"
          >
            Update Domain Metadata
          </v-btn>
        </div>
      </template>
    </v-card-text>
  </v-card>

  <v-alert
    v-if="errorMessage"
    type="error"
    variant="tonal"
    class="mb-4"
    :text="errorMessage"
    closable
    @click:close="errorMessage = ''"
  />
  <v-alert
    v-if="successMessage"
    key="success"
    type="success"
    variant="tonal"
    class="mb-4"
    :text="successMessage"
    closable
    @click:close="successMessage = ''"
  />
</template>

<script lang="ts">
import { ProgramEnvironmentMetadata } from '@policyfly/protobuf/pfbingo'
import { storeToRefs } from 'pinia'
import { computed, defineComponent, ref, useTemplateRef, watch } from 'vue'

import ProgramMetadataField from '@/components/ProgramMetadataField.vue'
import { useApiStore } from '@/stores/api'
import { useDomainStore } from '@/stores/domain'
import { useEnvironmentStore } from '@/stores/environment'

import type { Program, ReadProgramEnvironmentMetadataRequest } from '@policyfly/protobuf/pfbingo'
import type { PropType } from 'vue'

export default defineComponent({
  name: 'ProgramEnvironmentMetadata',

  components: {
    ProgramMetadataField,
  },

  props: {
    program: { type: Object as PropType<Program | null>, default: null },
  },

  setup (props) {
    const apiStore = useApiStore()
    const { currentDomain } = storeToRefs(useDomainStore())
    const { currentEnvironment } = storeToRefs(useEnvironmentStore())

    const loading = ref(true)
    const loadedMetadata = ref<ProgramEnvironmentMetadata | null>(null)
    const localMetadata = ref<ProgramEnvironmentMetadata | null>(null)
    const errorMessage = ref('')
    const successMessage = ref('')
    function resetForm (metadata: ProgramEnvironmentMetadata): void {
      loadedMetadata.value = metadata
      localMetadata.value = ProgramEnvironmentMetadata.clone(metadata)
      errorMessage.value = ''
      successMessage.value = ''
    }

    const hasUnsavedChanges = computed(() => {
      return !ProgramEnvironmentMetadata.equals(
        loadedMetadata.value ?? undefined,
        localMetadata.value ?? undefined,
      )
    })

    async function loadMetadata (request: ReadProgramEnvironmentMetadataRequest): Promise<void> {
      loading.value = true
      try {
        const { metadata } = await apiStore.loadProgramEnvironmentMetadata(request)
        // unset metadata is null, so create a starting version
        resetForm(metadata ?? ProgramEnvironmentMetadata.create())
      } catch (err) {
        console.error(err)
        errorMessage.value = err instanceof Error ? err.message : 'Failed to load program.'
        loadedMetadata.value = null
        localMetadata.value = null
      } finally {
        loading.value = false
      }
    }

    watch(() => ({
      domain: currentDomain.value,
      program: props.program,
      environmentId: currentEnvironment.value?.id,
    }), async ({ domain, program, environmentId }) => {
      if (!program || !environmentId) return
      await loadMetadata({
        domain,
        environmentId,
        programId: program.id,
      })
    }, { immediate: true })

    const saving = ref(false)
    const metadataFieldRef = useTemplateRef<typeof ProgramMetadataField>('metadataFieldRef')
    const saveDisabled = computed(() => {
      return loading.value || saving.value || !hasUnsavedChanges.value || !metadataFieldRef.value?.valid
    })
    async function saveMetadata (): Promise<void> {
      if (!localMetadata.value) return
      errorMessage.value = ''
      successMessage.value = ''
      saving.value = true
      try {
        await apiStore.updateProgramEnvironmentMetadata({
          domain: currentDomain.value,
          environmentId: currentEnvironment.value!.id,
          programId: props.program!.id,
          metadata: localMetadata.value,
        })
        resetForm(localMetadata.value)
        successMessage.value = `Saved metadata for "${props.program?.name}".`
      } catch (err) {
        console.error(err)
        errorMessage.value = err instanceof Error ? err.message : 'Failed to load program.'
      } finally {
        saving.value = false
      }
    }

    return {
      ProgramEnvironmentMetadata,
      currentEnvironment,

      loading,
      loadedMetadata,
      localMetadata,
      errorMessage,
      successMessage,
      resetForm,
      hasUnsavedChanges,

      saving,
      metadataFieldRef,
      saveDisabled,
      saveMetadata,
    }
  },
})
</script>
